import { PublicInternalAnalyticsTrackingSerializer } from "@shared/modules/analytics/types";
import { enums } from "@shared/constants";
import { isEmpty } from "lodash";
import { normalizeUrl } from "@shared/utils/urls/url-utils";
import { SITE_URL } from "@shared/env";
import { IInteractionDataBuilder } from "./IInteractionDataBuilder";

/**
 * Builder for tracking page views across the application.
 *
 * Key features:
 * - Automatically detects common pages (home, city, category)
 * - Extracts meaningful data from URLs
 * - Supports custom labels and parameters
 *
 * Business Logic:
 * - Home page gets special tracking
 * - Discovery pages (city/category) extract location and category data
 * - Custom parameters can be added for special page types
 * - URLs are normalized to handle variations
 *
 * Common usage:
 * ```ts
 * // Basic page view
 * await UserInteractionLogger.pageView();
 *
 * // Page view with custom data
 * await UserInteractionLogger.pageView({
 *   label: 'custom_page',
 *   custom_params: { key: 'value' }
 * });
 * ```
 */
export class PageViewDataBuilder implements IInteractionDataBuilder {
  private data: Partial<
    Omit<PublicInternalAnalyticsTrackingSerializer, "interaction_type">
  >;

  private routeUrl?: string;

  constructor(
    data: Partial<
      Omit<PublicInternalAnalyticsTrackingSerializer, "interaction_type">
    > = {},
    routeUrl?: string
  ) {
    this.data = data;
    this.routeUrl = routeUrl;
  }

  build(): PublicInternalAnalyticsTrackingSerializer | null {
    const newData = { ...this.data };

    if ((isEmpty(this.data) || !this.data.label) && this.routeUrl) {
      // Home page check
      if (
        normalizeUrl(this.routeUrl) === normalizeUrl(SITE_URL) ||
        this.routeUrl === "/"
      ) {
        newData.label = enums.INTERACTION_LABEL_MAP.HOME_PAGE.id;
      } else {
        // Extract discovery data from the route
        const match = this.routeUrl.match(/discover\/([^/]+)(?:\/([^/]+))?/);
        if (match) {
          const [, city, category] = match;
          let custom_params;
          if (category) {
            custom_params = { city, category };
            newData.label = enums.INTERACTION_LABEL_MAP.CATEGORY_PAGE.id;
          } else if (city) {
            custom_params = { city };
            newData.label = enums.INTERACTION_LABEL_MAP.CITY_PAGE.id;
          }
          newData.custom_params = custom_params;
        }
      }
    }

    return {
      ...newData,
      interaction_type: enums.INTERACTION_TYPE_MAP.PAGE_VIEW.id,
    };
  }
}
